/**
 * Authenticated fetch wrapper for Pieter Studio API calls.
 *
 * Usage:
 *   const data = await apiFetch("/api/videos/play-url?gcsPath=...");
 *   const data = await apiFetch("/api/videos/upload-url", {
 *     method: "POST",
 *     body: JSON.stringify({ ... }),
 *   });
 */

import { getAuthToken } from "./auth";

export const API_BASE =
	process.env.NEXT_PUBLIC_API_BASE ||
	"https://pieter-studio-api-163667449624.us-central1.run.app";

export async function apiFetch(
	endpoint: string,
	options: RequestInit = {},
): Promise<Response> {
	const token = await getAuthToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		Authorization: `Bearer ${token}`,
		...(options.headers as Record<string, string> || {}),
	};

	const response = await fetch(`${API_BASE}${endpoint}`, {
		...options,
		headers,
	});

	if (response.status === 401) {
		throw new Error("Session expired");
	}
	if (response.status === 403) {
		throw new Error("Access not approved");
	}

	return response;
}

/** Convenience: apiFetch + parse JSON */
export async function apiFetchJson<T = unknown>(
	endpoint: string,
	options: RequestInit = {},
): Promise<T> {
	const response = await apiFetch(endpoint, options);
	if (!response.ok) {
		const body = await response.json().catch(() => ({}));
		throw new Error((body as { error?: string }).error || `HTTP ${response.status}`);
	}
	return response.json() as Promise<T>;
}
